import { useEffect, useState } from "react";
import { Globe, Menu } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LANGUAGES, useLang } from "@/i18n";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/site";
import { Logo } from "./Primitives";

const SECTIONS = ["services", "rashifal", "stories", "kundali", "milan", "panchang", "contact"];
const PAGES = new Set(["kundali", "milan", "panchang"]);
const navLabel = (t, s) => t.nav2[s] || t.nav[s];

const useGo = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  return (s) => {
    if (PAGES.has(s)) nav(`/${s}`);
    else if (pathname === "/") scrollToId(s);
    else nav(`/#${s}`);
  };
};

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  const cur = LANGUAGES.find((l) => l.code === lang);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="language-switcher" className="pill-ghost flex items-center gap-2 px-3.5 py-2 text-sm">
        <Globe className="w-4 h-4" /> {cur.short}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#fffdf9] border-[color:var(--line)] text-[color:var(--ink)]">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} data-testid={`language-option-${l.code}`} onClick={() => setLang(l.code)}
            className={`cursor-pointer focus:bg-black/5 focus:text-[color:var(--ink)] ${l.code === lang ? "text-[color:var(--saffron)]" : ""}`}>
            {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileNav = ({ t }) => {
  const go = useGo();
  return (
  <DropdownMenu>
    <DropdownMenuTrigger data-testid="mobile-nav-toggle" className="xl:hidden pill-ghost p-2.5" aria-label="Menu"><Menu className="w-4 h-4" /></DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="bg-[#fffdf9] border-[color:var(--line)] text-[color:var(--ink)] min-w-44">
      {SECTIONS.map((s) => (
        <DropdownMenuItem key={s} data-testid={`mobile-nav-${s}`} onClick={() => go(s)} className="cursor-pointer focus:bg-black/5 focus:text-[color:var(--ink)]">{navLabel(t, s)}</DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [solid, setSolid] = useState(false);
  const go = useGo();
  useEffect(() => {
    const on = () => setSolid(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header data-testid="site-header" className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 border-b ${solid ? "bg-[#fbf8f3]/85 backdrop-blur-xl border-[color:var(--line)]" : "border-transparent"}`}>
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <button data-testid="header-logo" onClick={() => go("top")} className="flex items-center gap-3 group">
          <span className="w-12 h-12 rounded-full bg-white/90 shadow-sm flex items-center justify-center"><Logo className="w-9 h-9 transition-transform duration-700 group-hover:scale-110" /></span>
          <span className="display text-xl leading-none hidden sm:block">Vedic Yagya <em className="text-[color:var(--gold)]">Astrology</em></span>
        </button>
        <nav className="hidden xl:flex items-center gap-7 text-sm text-[color:var(--muted)]">
          {SECTIONS.map((s) => (
            <button key={s} data-testid={`nav-${s}`} onClick={() => go(s)} className="nav-link hover:text-[color:var(--ink)] transition-colors">{navLabel(t, s)}</button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangSwitch />
          <Link data-testid="header-book-btn" to="/book" className="pill-solid hidden sm:inline-flex px-5 py-2 text-sm">{t.nav.book}</Link>
          <MobileNav t={t} />
        </div>
      </div>
    </header>
  );
};
